import type { JobSeekerProfileLocationTypeInput } from "../../../../../../../__generated__/types.gqlgenerated";

import { useGetJobSeekerProfileLocationLazyQuery } from "./graphql/__generated__/useGetJobSeekerProfileLocation.gqlgenerated";
import { useGetMaximumCommutePreferenceLazyQuery } from "./graphql/__generated__/useGetMaximumCommutePreference.gqlgenerated";
import { useAddMaximumCommutePreference } from "./graphql/useAddMaximumCommutePreference";
import { useUpdateJobSeekerLocation } from "./graphql/useUpdateJobSeekerLocation";
import { MaxCommuteSelect } from "./MaxCommuteSelect";
import {
  type LocationInputComponentResult,
  type PrefillLocation,
  BottomSheetModalProvider,
  LocationInputComponent,
} from "../../../../../../../location-input-component";
import {
  Alert,
  Box,
  Button,
  Divider,
  Heading,
  Pressable,
  ScrollView,
  Text,
} from "../../../../../../../mads";
import { Close } from "../../../../../../../mads/icons";
import { serializeLocaleToBCP47, useI18n } from "../../../../../../../shared/I18n";
import {
  type UserLocation,
  useStoredUserLocation,
} from "../../../../../../../shared/location/useStoredUserLocation";
import { useLogger } from "../../../../../../../shared/logging/logger";
import { useTareData } from "../../../../../../../shared/native-modules/tare/TareDataContext";
import { useIsStructuredValidationEnabled } from "../../../../../hooks/useIsStructuredValidationEnabled";
import { extractLocale } from "../../../../../util/extractLocale";

import { colors } from "@indeed/ifl-tokens/react-native";
import { useNavigation } from "@react-navigation/native";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

/**
 * Builds a `PrefillLocation` from any source object that exposes the same
 * shape as `UserLocation` / `JobSeekerProfileLocationType`. Returns
 * `undefined` when there's no street address — the LIC can't anchor a
 * meaningful prefill without one.
 */
function buildPrefill(
  streetAddress: string | undefined | null,
  fields:
    | {
        city?: string | null;
        admin1?: string | null;
        admin2?: string | null;
        admin3?: string | null;
        admin4?: string | null;
        postalCode?: string | null;
        latitude?: number | null;
        longitude?: number | null;
      }
    | null
    | undefined,
): PrefillLocation | undefined {
  if (!streetAddress) return undefined;
  return {
    streetAddress,
    city: fields?.city ?? undefined,
    admin1: fields?.admin1 ?? undefined,
    admin2: fields?.admin2 ?? undefined,
    admin3: fields?.admin3 ?? undefined,
    admin4: fields?.admin4 ?? undefined,
    postalCode: fields?.postalCode ?? undefined,
    latitude: fields?.latitude ?? undefined,
    longitude: fields?.longitude ?? undefined,
  };
}

type CommutePreferenceModalProps = {
  isDesktop?: boolean;
};

export function CommutePreferenceModal({ isDesktop = false }: CommutePreferenceModalProps) {
  const gt = useI18n();
  const logger = useLogger();
  const navigation = useNavigation();
  const tareData = useTareData();
  const isStructuredValidationEnabled = useIsStructuredValidationEnabled();

  const { storedLocation, updateStoredLocation } = useStoredUserLocation();

  const updateJobSeekerLocation = useUpdateJobSeekerLocation();
  const addMaximumCommutePreference = useAddMaximumCommutePreference();
  const [fetchMaxCommute, { data: maxCommuteData }] = useGetMaximumCommutePreferenceLazyQuery({
    fetchPolicy: "cache-first",
  });
  const [fetchProfileLocation, { data: profileLocationData }] =
    useGetJobSeekerProfileLocationLazyQuery({ fetchPolicy: "cache-first" });

  useEffect(() => {
    void fetchMaxCommute();
    void fetchProfileLocation();
  }, [fetchMaxCommute, fetchProfileLocation]);

  // Prefer the resume location when a resume exists
  const profile = profileLocationData?.jobSeekerProfile?.profile;
  const mainResume = profile?.resume?.[0];
  const resumeId = mainResume?.id;
  const sourceLocation = resumeId
    ? mainResume?.location
    : profile?.defaultInfo?.contactInformation?.location;

  // Prefer `storedLocation` for prefill so the inputs populate synchronously
  // on first render. Falls back to the server profile data when localPrefs
  // is empty
  const profilePrefill = useMemo<PrefillLocation | undefined>(
    () =>
      buildPrefill(storedLocation?.streetAddress, storedLocation) ??
      buildPrefill(sourceLocation?.address, sourceLocation),
    [storedLocation, sourceLocation],
  );

  const serverMaxCommute = useMemo(() => {
    const preferences = maxCommuteData?.jobSeekerProfileStructuredData?.preferences ?? [];
    for (const pref of preferences) {
      const minutes = pref?.maximumCommute?.timeMinutes;
      if (minutes && minutes > 0) {
        return minutes;
      }
    }
    return undefined;
  }, [maxCommuteData]);

  const [selectedLocation, setSelectedLocation] = useState<
    LocationInputComponentResult | undefined
  >(undefined);
  const [maxCommute, setMaxCommute] = useState<number | undefined>(() => {
    const stored = storedLocation?.maxCommuteMinutes;
    return stored && stored > 0 ? stored : undefined;
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // `initialMaxCommute` is the source-of-truth for "what value did the
  // modal load with" — used to decide if the save button should be enabled.
  const initialMaxCommute =
    (storedLocation?.maxCommuteMinutes && storedLocation.maxCommuteMinutes > 0
      ? storedLocation.maxCommuteMinutes
      : undefined) ?? serverMaxCommute;

  useEffect(() => {
    if (
      serverMaxCommute !== undefined &&
      serverMaxCommute !== maxCommute &&
      maxCommute === (storedLocation?.maxCommuteMinutes ?? undefined)
    ) {
      setMaxCommute(serverMaxCommute);
    }
  }, [serverMaxCommute, maxCommute, storedLocation?.maxCommuteMinutes]);

  useEffect(() => {
    if (initialMaxCommute === undefined) return;
    if (!storedLocation) return;
    if (storedLocation.maxCommuteMinutes === initialMaxCommute) return;
    void updateStoredLocation({
      ...storedLocation,
      maxCommuteMinutes: initialMaxCommute,
    });
  }, [initialMaxCommute, storedLocation, updateStoredLocation]);

  const localeObj = useMemo(
    () => extractLocale(tareData?.appModel ?? undefined, undefined),
    [tareData?.appModel],
  );
  const country = localeObj.country ?? "US";
  const language = localeObj.language;
  const localeString = serializeLocaleToBCP47(localeObj);

  const hasAddress = !!(selectedLocation?.location || profilePrefill);

  const handleClose = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const handleSave = useCallback(async () => {
    const gen2 = selectedLocation?.location;
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const serverMutations: Promise<unknown>[] = [];

      let localPrefsWrite: UserLocation | undefined;

      // Only update the profile location if the user picked a fresh
      // address. Changing max commute alone leaves the address intact.
      if (gen2?.latitude != null) {
        const streetAddress = gen2.preciseLocation?.name ?? undefined;
        const sharedLocationFields = {
          city: gen2.admin5?.name ?? undefined,
          admin1: gen2.admin1?.code ?? undefined,
          admin2: gen2.admin2?.code ?? undefined,
          admin3: gen2.admin3?.code ?? undefined,
          admin4: gen2.admin4?.code ?? undefined,
          postalCode: gen2.postalCode?.name ?? undefined,
          country: gen2.country?.code ?? country,
          latitude: gen2.latitude,
          longitude: gen2.longitude,
          geocodePrecision: "ADDRESS" as const,
        };

        const profileInput: JobSeekerProfileLocationTypeInput = {
          ...sharedLocationFields,
          address: streetAddress,
        };

        serverMutations.push(updateJobSeekerLocation(profileInput, resumeId));
        localPrefsWrite = {
          ...sharedLocationFields,
          streetAddress,
          formattedLocation: selectedLocation?.formattedName,
          maxCommuteMinutes: maxCommute ?? null,
        };
      } else if (
        maxCommute !== undefined &&
        storedLocation &&
        storedLocation.maxCommuteMinutes !== maxCommute
      ) {
        // Commute-only edit: refresh localPrefs without touching the
        // address fields.
        localPrefsWrite = {
          ...storedLocation,
          maxCommuteMinutes: maxCommute,
        };
      }

      if (maxCommute !== undefined && maxCommute !== initialMaxCommute) {
        serverMutations.push(addMaximumCommutePreference(maxCommute));
      }

      await Promise.all(serverMutations);
      if (localPrefsWrite) {
        await updateStoredLocation(localPrefsWrite);
      }

      logger.event.trackEvent("interactiveAttributeInteraction", {
        action: "SAVE",
        section: "location",
      });

      handleClose();
    } catch {
      setErrorMessage(gt.gettext("Something went wrong. Please try again."));
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedLocation,
    maxCommute,
    initialMaxCommute,
    country,
    resumeId,
    storedLocation,
    updateJobSeekerLocation,
    addMaximumCommutePreference,
    updateStoredLocation,
    logger,
    handleClose,
    gt,
  ]);

  // LIC signals a user-initiated clear with `formattedName: ''` and no
  // location. Other "no result" callbacks from LIC (e.g. prefill
  // validation failure) leave formattedName undefined, so we key off the
  // explicit empty string to ignore the profile prefill only when the
  // user has actively removed the value.
  const userClearedAddress =
    selectedLocation !== undefined &&
    selectedLocation.formattedName === "" &&
    selectedLocation.location?.latitude == null;
  const hasFreshAddress = selectedLocation?.location?.latitude != null;
  const hasExistingAddress = !!profilePrefill && !userClearedAddress;
  const maxCommuteChanged = maxCommute !== undefined && maxCommute !== initialMaxCommute;
  const canSave = !isSaving && (hasFreshAddress || (hasExistingAddress && maxCommuteChanged));

  const descriptionTextLevel = isDesktop ? 2 : 3;
  const fields = (
    <>
      <Text level={descriptionTextLevel}>
        {gt.gettext(
          "This helps estimate commute time. Include your street address for more accurate estimates.",
        )}
      </Text>

      <Box sx={isDesktop ? desktopStyles.locationField : styles.field}>
        <LocationInputComponent
          label={gt.gettext("Your address")}
          helperText={gt.gettext("Employers may see your city and state.")}
          placeholder={gt.gettext("Enter your address")}
          options={{
            country,
            language,
            locationTypeFilter: ["PRECISE_LOCATION"],
          }}
          locale={localeString}
          prefill={profilePrefill}
          preciseLocation={true}
          useStructuredValidation={isStructuredValidationEnabled}
          onValueSelected={(result) => {
            setSelectedLocation(result);
          }}
          clientPage="rn-viewjob"
          // On desktop, float the suggestions dropdown above the
          // rest of the modal content instead of pushing the Max
          // commute field down.
          optionsWrapperStyle={isDesktop ? desktopStyles.locationDropdown : undefined}
        />
      </Box>

      <Box sx={styles.field}>
        <MaxCommuteSelect
          value={maxCommute}
          onChange={setMaxCommute}
          disabled={!hasAddress}
          isDesktopDisplay={isDesktop}
        />
      </Box>

      {errorMessage && <Alert status="critical" explainerText={errorMessage} sx={styles.alert} />}
    </>
  );

  if (isDesktop) {
    return (
      <BottomSheetModalProvider>
        <Pressable
          sx={desktopStyles.backdrop}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel={gt.gettext("Close modal")}
        />
        <Box sx={desktopStyles.cardContainer} pointerEvents="box-none">
          <Box sx={desktopStyles.card}>
            <Box sx={desktopStyles.headerRow}>
              <Heading level={4}>{gt.gettext("Edit location preferences")}</Heading>
              <Pressable
                onPress={handleClose}
                accessibilityRole="button"
                accessibilityLabel={gt.gettext("Close modal")}
                sx={desktopStyles.closeButton}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
              >
                <Close size="md" fill={colors.foregroundStrong} />
              </Pressable>
            </Box>
            <Box sx={desktopStyles.body}>{fields}</Box>
            <Box sx={desktopStyles.footer}>
              <Button variant="secondary" onPress={handleClose}>
                {gt.gettext("Cancel")}
              </Button>
              <Button
                variant="primary"
                onPress={() => {
                  void handleSave();
                }}
                disabled={!canSave}
                loading={isSaving}
              >
                {gt.gettext("Save to profile")}
              </Button>
            </Box>
          </Box>
        </Box>
      </BottomSheetModalProvider>
    );
  }

  return (
    <BottomSheetModalProvider>
      <SafeAreaView style={styles.sheet} edges={["bottom"]}>
        <Box sx={styles.headerRow}>
          <Heading level={3}>{gt.gettext("Edit location preferences")}</Heading>
          <Pressable
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel={gt.gettext("Close modal")}
            sx={styles.closeButton}
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          >
            <Close size="md" fill={colors.foregroundStrong} />
          </Pressable>
        </Box>
        <Divider />

        <ScrollView sx={styles.scrollView} contentContainerSx={styles.scrollContent}>
          {fields}
        </ScrollView>

        <Divider />
        <Box sx={styles.footer}>
          <Button
            variant="primary"
            onPress={() => {
              void handleSave();
            }}
            disabled={!canSave}
            loading={isSaving}
          >
            {gt.gettext("Save to profile")}
          </Button>
          <Button variant="secondary" onPress={handleClose}>
            {gt.gettext("Cancel")}
          </Button>
        </Box>
      </SafeAreaView>
    </BottomSheetModalProvider>
  );
}

const styles = StyleSheet.create({
  sheet: {
    flex: 1,
    backgroundColor: colors.background,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
  },
  closeButton: {
    padding: 4,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 20,
    gap: 20,
  },
  field: {
    gap: 8,
  },
  alert: {
    marginTop: 8,
  },
  footer: {
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
});

const desktopStyles = StyleSheet.create({
  backdrop: {
    // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.utilityOverlay,
    zIndex: 1,
  },
  cardContainer: {
    // @ts-expect-error - position 'fixed' is valid on web (react-native-web)
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  card: {
    backgroundColor: colors.background,
    borderRadius: 12,
    width: "90%",
    maxWidth: 600,
    shadowColor: colors.utilityShadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  locationField: {
    position: "relative",
    zIndex: 1,
    gap: 8,
  },
  locationDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    // LIC's internal `optionsTable` merge sets borderWidth: 0, so the
    // default 1px border from `optionsWrapper` is lost on the way in.
    // Restore a subtle border so the floating dropdown reads as a
    // distinct popover layered above the modal content.
    borderWidth: 1,
    borderColor: colors.borderWeak,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    minHeight: 64,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 16,
  },
  body: {
    paddingHorizontal: 24,
    gap: 4,
    position: "relative",
    zIndex: 2,
  },
  closeButton: {
    padding: 8,
    borderRadius: 8,
    marginRight: -8,
    marginTop: -8,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 16,
    position: "relative",
    zIndex: 1,
  },
});
